import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';

const emptyRegisterForm = {
  username: '',
  password: '',
  name: '',
  title: '',
  specialty: '',
  license_number: ''
};

const emptyEditForm = {
  username: '',
  name: '',
  title: '',
  specialty: '',
  license_number: ''
};

const DoctorList = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    doctorId: null,
    doctorName: ''
  });
  const [editingDoctor, setEditingDoctor] = useState({
    show: false,
    doctorId: null,
    form: emptyEditForm
  });

  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/doctors', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctors');
      }

      const data = await response.json();
      setDoctors(data);
      setError('');
    } catch (requestError) {
      console.error('Error fetching doctors:', requestError);
      setError(t('doctors.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleRegisterChange = (field, value) => {
    setRegisterForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();

    if (!registerForm.username || !registerForm.password || !registerForm.name) {
      setError(t('doctors.requiredFields'));
      return;
    }

    try {
      const response = await fetch('/api/v1/doctors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(registerForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      setRegisterForm(emptyRegisterForm);
      setShowRegister(false);
      setError('');
      fetchDoctors();
    } catch (requestError) {
      console.error('Registration error:', requestError);
      setError(`${t('doctors.registerError')}: ${requestError.message}`);
    }
  };

  const handleEdit = (doctorId) => {
    const doctor = doctors.find((item) => item.id === doctorId);
    if (!doctor) {
      return;
    }

    setEditingDoctor({
      show: true,
      doctorId,
      form: {
        username: doctor.username || '',
        name: doctor.name || '',
        title: doctor.title || '',
        specialty: doctor.specialty || '',
        license_number: doctor.license_number || ''
      }
    });
  };

  const handleEditChange = (field, value) => {
    setEditingDoctor((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value
      }
    }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(`/api/v1/doctors/${editingDoctor.doctorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(editingDoctor.form)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Update failed');
      }

      setEditingDoctor({ show: false, doctorId: null, form: emptyEditForm });
      setError('');
      fetchDoctors();
    } catch (requestError) {
      console.error('Edit error:', requestError);
      setError(`${t('doctors.editError')}: ${requestError.message}`);
    }
  };

  const handleDelete = (doctorId) => {
    const doctor = doctors.find((item) => item.id === doctorId);
    setDeleteConfirm({
      show: true,
      doctorId,
      doctorName: doctor?.name || 'Unknown'
    });
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`/api/v1/doctors/${deleteConfirm.doctorId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete doctor');
      }

      setDeleteConfirm({ show: false, doctorId: null, doctorName: '' });
      setDoctors((prev) => prev.filter((doctor) => doctor.id !== deleteConfirm.doctorId));
      setError('');
    } catch (requestError) {
      console.error('Error deleting doctor:', requestError);
      setError(t('doctors.deleteError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return t('dashboard.notSet');
    }

    return new Date(dateString).toLocaleString(isZh ? 'zh-CN' : 'en-US');
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <span className="page-eyebrow">{t('dashboard.doctorManagement')}</span>
          <h1 className="page-title">{t('doctors.list')}</h1>
          <p className="page-subtitle">
            {isZh
              ? '管理员侧保留医生账号的创建、维护和删除入口，风格与患者端和医生端工作台保持一致。'
              : 'The admin surface keeps doctor account creation, maintenance, and removal in one consistent workspace that matches the patient and doctor consoles.'}
          </p>
        </div>

        <div className="page-actions">
          <button
            type="button"
            className="theme-button theme-button--secondary"
            onClick={() => setShowRegister((prev) => !prev)}
          >
            {showRegister ? t('doctors.cancelRegister') : t('doctors.register')}
          </button>
          <button
            type="button"
            className="theme-button theme-button--ghost"
            onClick={fetchDoctors}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {showRegister && (
        <section className="surface-panel surface-panel--soft">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <span className="page-eyebrow">{t('doctors.register')}</span>
              <h2 className="report-panel-title">{t('doctors.registerNew')}</h2>
            </div>

            <form onSubmit={handleRegisterSubmit} className="stack">
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="register-username">{t('auth.username')}</label>
                  <input
                    id="register-username"
                    className="theme-input"
                    type="text"
                    value={registerForm.username}
                    onChange={(event) => handleRegisterChange('username', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-password">{t('auth.password')}</label>
                  <input
                    id="register-password"
                    className="theme-input"
                    type="password"
                    value={registerForm.password}
                    onChange={(event) => handleRegisterChange('password', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-name">{t('auth.name')}</label>
                  <input
                    id="register-name"
                    className="theme-input"
                    type="text"
                    value={registerForm.name}
                    onChange={(event) => handleRegisterChange('name', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-title">{t('doctors.title')}</label>
                  <input
                    id="register-title"
                    className="theme-input"
                    type="text"
                    value={registerForm.title}
                    onChange={(event) => handleRegisterChange('title', event.target.value)}
                    placeholder={t('doctors.titlePlaceholder')}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-specialty">{t('doctors.specialty')}</label>
                  <input
                    id="register-specialty"
                    className="theme-input"
                    type="text"
                    value={registerForm.specialty}
                    onChange={(event) => handleRegisterChange('specialty', event.target.value)}
                    placeholder={t('doctors.specialtyPlaceholder')}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-license">{t('doctors.licenseNumber')}</label>
                  <input
                    id="register-license"
                    className="theme-input"
                    type="text"
                    value={registerForm.license_number}
                    onChange={(event) => handleRegisterChange('license_number', event.target.value)}
                    placeholder={t('doctors.licenseNumberPlaceholder')}
                  />
                </div>
              </div>

              <div className="page-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="theme-button theme-button--success">
                  {t('doctors.register')}
                </button>
                <button
                  type="button"
                  className="theme-button theme-button--ghost"
                  onClick={() => setShowRegister(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {editingDoctor.show && (
        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <span className="page-eyebrow">{t('doctors.edit')}</span>
              <h2 className="report-panel-title">{t('doctors.editDoctor')}</h2>
            </div>

            <form onSubmit={handleEditSubmit} className="stack">
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="edit-username">{t('auth.username')}</label>
                  <input
                    id="edit-username"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.username}
                    onChange={(event) => handleEditChange('username', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-name">{t('auth.name')}</label>
                  <input
                    id="edit-name"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.name}
                    onChange={(event) => handleEditChange('name', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-title">{t('doctors.title')}</label>
                  <input
                    id="edit-title"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.title}
                    onChange={(event) => handleEditChange('title', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-specialty">{t('doctors.specialty')}</label>
                  <input
                    id="edit-specialty"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.specialty}
                    onChange={(event) => handleEditChange('specialty', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-license">{t('doctors.licenseNumber')}</label>
                  <input
                    id="edit-license"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.license_number}
                    onChange={(event) => handleEditChange('license_number', event.target.value)}
                  />
                </div>
              </div>

              <div className="page-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="theme-button theme-button--primary">
                  {t('doctors.update')}
                </button>
                <button
                  type="button"
                  className="theme-button theme-button--ghost"
                  onClick={() => setEditingDoctor({ show: false, doctorId: null, form: emptyEditForm })}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--success">
              {isZh ? `医生 ${doctors.length} 位` : `${doctors.length} doctors`}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : doctors.length === 0 ? (
            <div className="empty-state">
              <h3 className="auth-form__title">{t('doctors.noDoctors')}</h3>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t('auth.username')}</th>
                    <th>{t('auth.name')}</th>
                    <th>{t('doctors.title')}</th>
                    <th>{t('doctors.specialty')}</th>
                    <th>{t('doctors.licenseNumber')}</th>
                    <th>{t('doctors.createdAt')}</th>
                    <th>{t('doctors.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((doctor) => (
                    <tr key={doctor.id}>
                      <td>
                        <div className="table-title">#{doctor.id}</div>
                      </td>
                      <td>{doctor.username}</td>
                      <td>{doctor.name}</td>
                      <td>{doctor.title || t('dashboard.notSet')}</td>
                      <td>{doctor.specialty || t('dashboard.notSet')}</td>
                      <td>{doctor.license_number || t('dashboard.notSet')}</td>
                      <td>{formatDate(doctor.created_at)}</td>
                      <td>
                        <div className="doctor-table__actions">
                          <button
                            type="button"
                            className="theme-button theme-button--ghost"
                            style={{ minHeight: '2.4rem', padding: '0.5rem 0.95rem' }}
                            onClick={() => handleEdit(doctor.id)}
                          >
                            {t('doctors.edit')}
                          </button>
                          <button
                            type="button"
                            className="theme-button theme-button--danger"
                            style={{ minHeight: '2.4rem', padding: '0.5rem 0.95rem' }}
                            onClick={() => handleDelete(doctor.id)}
                          >
                            {t('doctors.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, doctorId: null, doctorName: '' })}
        onConfirm={confirmDelete}
        title={t('doctors.confirmDelete')}
        message={t('doctors.confirmDeleteMessage', { name: deleteConfirm.doctorName })}
        confirmText={t('doctors.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </div>
  );
};

export default DoctorList;
